import { ReportDetailsFormData } from './types';
import { REPORT_FORM_VALIDATION } from './constants';
import { isInputValid } from '../../../utils/validation/inputValidation';

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Validates the report form data
 * @param formData The form data to validate
 * @returns An object with validation results
 */
export const validateReportForm = (formData: ReportDetailsFormData): { 
  isValid: boolean; 
  errors: Record<string, string>;
} => {
  const errors: Record<string, string> = {};
  
  // Validate title
  if (!isInputValid(formData.title, REPORT_FORM_VALIDATION.title)) {
    if (!formData.title.trim()) {
      errors.title = "Report title is required";
    } else if (formData.title.length > REPORT_FORM_VALIDATION.title.maxLength) {
      errors.title = `Title must be at most ${REPORT_FORM_VALIDATION.title.maxLength} characters`;
    } else if (
      REPORT_FORM_VALIDATION.title.minLength && 
      formData.title.length < REPORT_FORM_VALIDATION.title.minLength
    ) {
      errors.title = `Title must be at least ${REPORT_FORM_VALIDATION.title.minLength} characters`;
    }
  }
  
  // Validate author name
  if (!isInputValid(formData.authorName, REPORT_FORM_VALIDATION.authorName)) {
    if (!formData.authorName.trim()) {
      errors.authorName = "Name is required";
    } else if (formData.authorName.length > REPORT_FORM_VALIDATION.authorName.maxLength) {
      errors.authorName = `Name must be at most ${REPORT_FORM_VALIDATION.authorName.maxLength} characters`;
    } else if (
      REPORT_FORM_VALIDATION.authorName.minLength && 
      formData.authorName.length < REPORT_FORM_VALIDATION.authorName.minLength
    ) {
      errors.authorName = `Name must be at least ${REPORT_FORM_VALIDATION.authorName.minLength} characters`;
    }
  }
  
  // Validate author role (optional)
  if (formData.authorRole && !isInputValid(formData.authorRole, REPORT_FORM_VALIDATION.authorRole)) {
    if (formData.authorRole.length > REPORT_FORM_VALIDATION.authorRole.maxLength) {
      errors.authorRole = `Role must be at most ${REPORT_FORM_VALIDATION.authorRole.maxLength} characters`;
    }
  }
  
  // Validate email
  if (!isInputValid(formData.authorEmail, REPORT_FORM_VALIDATION.email)) {
    if (!formData.authorEmail.trim()) {
      errors.authorEmail = "Email is required";
    } else if (formData.authorEmail.length > REPORT_FORM_VALIDATION.email.maxLength) {
      errors.authorEmail = `Email must be at most ${REPORT_FORM_VALIDATION.email.maxLength} characters`;
    } else if (
      REPORT_FORM_VALIDATION.email.minLength && 
      formData.authorEmail.length < REPORT_FORM_VALIDATION.email.minLength
    ) {
      errors.authorEmail = `Email must be at least ${REPORT_FORM_VALIDATION.email.minLength} characters`;
    }
  }
  
  // Validate email format
  if (formData.authorEmail && !EMAIL_REGEX.test(formData.authorEmail)) {
    errors.authorEmail = "Please enter a valid email address";
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Helper function to compress image
export const compressImage = async (base64String: string, maxSizeKB: number = 500): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Calculate new dimensions while maintaining aspect ratio
      const maxDimension = 1200; // Max width or height
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // Start with high quality
      let quality = 0.9;
      let output = canvas.toDataURL('image/jpeg', quality);
      
      // Reduce quality until file size is under maxSizeKB
      while (output.length > maxSizeKB * 1024 && quality > 0.1) {
        quality -= 0.1;
        output = canvas.toDataURL('image/jpeg', quality);
      }
      
      resolve(output);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = base64String;
  });
};

// Helper function to get default sections based on project nodes
export const getDefaultSections = (currentProject: any): string[] => {
  const sections: string[] = [];
  
  // Always add main section categories (except TOPICS)
  sections.push('SOURCES');
  sections.push('ARCHITECTURE');
  sections.push('MODULES');
  sections.push('DESTINATIONS');
  
  if (currentProject && currentProject.nodes) {
    // Define source node types
    const sourceTypes = ['mobile-sdk', 'javascript-source', 'server-source'];
    
    // Add all source types regardless of whether they exist in the project
    sourceTypes.forEach(type => {
      sections.push(`SOURCES:${type}`);
      sections.push(type);
    });
    
    // Add all source nodes that exist in the project
    const sourceNodes = currentProject.nodes.filter(
      (node: any) => sourceTypes.includes(node.type)
    );
    
    sourceNodes.forEach((node: any) => {
      sections.push(`SOURCE:${node.id}`);
      sections.push(node.id);
    });
    
    // Define architecture node types
    const architectureTypes = ['sync-injector', 'cluster', 'load-balancer'];
    
    // Add all architecture types regardless of whether they exist in the project
    architectureTypes.forEach(type => {
      sections.push(`ARCHITECTURE:${type}`);
      sections.push(type);
    });
    
    // Add all architecture nodes that exist in the project
    const architectureNodes = currentProject.nodes.filter(
      (node: any) => architectureTypes.includes(node.type)
    );
    
    architectureNodes.forEach((node: any) => {
      sections.push(`ARCHITECTURE:${node.id}`);
      sections.push(node.id);
    });
    
    // Add all modules
    const enabledModules: string[] = [];
    currentProject.nodes.forEach((node: any) => {
      if (node.type === 'cluster' && node.data) {
        const clusterData = node.data as any;
        if (clusterData.modules) {
          clusterData.modules.forEach((m: any) => {
            if (m.enabled && m.moduleId) {
              enabledModules.push(m.moduleId);
            }
          });
        }
      }
    });
    
    enabledModules.forEach((moduleId) => {
      sections.push(`MODULE:${moduleId}`);
    });
    
    // Add all destinations
    const destinationNodes = currentProject.nodes.filter(
      (node: any) => node.type === 'destination'
    );
    
    destinationNodes.forEach((node: any) => {
      if (node.data) {
        sections.push(`DESTINATION:${node.id}`);
        sections.push(node.id);
        
        // Also add by name for compatibility
        if (node.data.name) {
          sections.push(node.data.name);
          sections.push(`DESTINATIONS:${node.data.name}`);
        }
      }
    });
  }
  
  return sections;
};
